import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { hasPermission } from '@/hooks/useBackendMenu'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import React from 'react'
import { Registro } from 'service/api'
import { useStore } from 'store'
import { match, P } from 'ts-pattern'
import Input from './Input'
import { useDigitacaoContext } from './pages/useDigitacaoContext'
import { usePeriodoContrato } from './usePeriodoContrato'
import { entradasSaidas, isElementVisible } from './utils/helpers'

const RegistrosCell: React.FC<GridCellParams<Registro[]>> = ({
    row,
    value,
}) => {
    const { view } = useDigitacaoContext()
    const funcionario = useStore(state => state.employee)
    const noPeridoDoContrato = usePeriodoContrato({ dia: row.dia })

    return (
        <div
            id={`Registros_${row.dia}`}
            onKeyDown={handleKeyDown}
            data-index={row.id}
            data-view={view}
            className="flex flex-col size-full justify-evenly items-center"
        >
            {Array.from({
                length: match<number>(row.registros.length)
                    .with(P.number.between(17, 24), () => 4)
                    .with(P.number.between(11, 16), () => 3)
                    .with(P.number.between(5, 10), () => 2)
                    .otherwise(() => 1),
            })
                .map((_, index) => index)
                .map(group => (
                    <div
                        key={`group_${group}`}
                        className="flex flex-row w-full justify-evenly items-center gap-1"
                    >
                        {entradasSaidas
                            .slice(group * 6, (group + 1) * 6)
                            .map((key, index) => {
                                const registro = value?.at(group * 6 + index)

                                return (
                                    <Input
                                        key={`input_${row.dia}_${key}`}
                                        dia={row.dia}
                                        diaRegistro={
                                            registro?.data
                                                ? dayjs.utc(registro.data)
                                                : undefined
                                        }
                                        entSai={key}
                                        origem={registro?.origem}
                                        status={registro?.statusAprovacao}
                                        registroId={registro?.id}
                                        defaultValue={registro?.hora}
                                        funcionarioId={
                                            view === 'mensal'
                                                ? funcionario?.id
                                                : row.funcionarioId
                                        }
                                        disabled={
                                            !hasPermission([
                                                'create',
                                                'Registros',
                                            ]) || noPeridoDoContrato
                                        }
                                        saidaDiaAnterior={
                                            registro?.saidaDiaAnterior === 'S'
                                        }
                                        entradaDiaSeguinte={
                                            registro?.entradaDiaSeguinte === 'S'
                                        }
                                    />
                                )
                            })}
                    </div>
                ))}
        </div>
    )
}

function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const isShiftTab = event.shiftKey && event.key === 'Tab'
    if (isShiftTab) {
        event.preventDefault()
        event.stopPropagation()
    }
    const value = (event.target as HTMLInputElement).value
    if ((event.key === 'Tab' || isShiftTab) && value.length === 0) {
        event.preventDefault()
        const index = event.currentTarget.dataset.index
        const view = event.currentTarget.dataset.view
        const [, data] = event.currentTarget.id.split('_')

        if (view === 'mensal') {
            const proximoDia = isShiftTab
                ? dayjs(data).subtract(1, 'day').format('YYYY-MM-DD')
                : dayjs(data).add(1, 'day').format('YYYY-MM-DD')
            const nextNotDisabled = document.querySelector(
                `#Registros_${proximoDia} input:not([disabled])`,
            ) as HTMLInputElement

            if (nextNotDisabled) {
                const isVisible = isElementVisible(nextNotDisabled)
                if (!isVisible) {
                    nextNotDisabled?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    })
                }

                nextNotDisabled?.focus()
            }
        } else {
            const nextIndex = isShiftTab ? Number(index) - 1 : Number(index) + 1
            const nextElement = document.querySelector(
                `[data-index="${nextIndex}"]`,
            ) as HTMLInputElement

            const nestInputNotDisabled = nextElement?.querySelector(
                'input:not([disabled])',
            ) as HTMLInputElement

            if (nestInputNotDisabled) {
                const isVisible = isElementVisible(nestInputNotDisabled)
                if (!isVisible) {
                    nestInputNotDisabled?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    })
                }

                nestInputNotDisabled?.focus()
            }
        }
    }
}

export default RegistrosCell
